import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { TrendingUpIcon, DollarSignIcon, CoinsIcon, BarChart3Icon, ActivityIcon, BrainCircuitIcon } from 'lucide-react';
const markets = [{
  icon: BarChart3Icon,
  name: 'Equities',
  description: 'Global equity markets across G7 countries and emerging markets',
  color: '#60A5FA'
}, {
  icon: DollarSignIcon,
  name: 'FX & Currencies',
  description: 'Major currency pairs and cross-currency arbitrage opportunities',
  color: '#14B8A6'
}, {
  icon: ActivityIcon,
  name: 'Commodities',
  description: 'Energy, metals, agriculture, and alternative commodity indices',
  color: '#F59E0B'
}, {
  icon: TrendingUpIcon,
  name: 'Futures & Derivatives',
  description: 'Options, futures contracts, and structured products',
  color: '#8B5CF6'
}, {
  icon: CoinsIcon,
  name: 'Crypto & Digital Assets',
  description: 'Bitcoin, Ethereum, DeFi protocols, and blockchain infrastructure',
  color: '#10B981'
}, {
  icon: BrainCircuitIcon,
  name: 'Prediction Markets',
  description: 'Polymarket, event-driven trading, and information markets',
  color: '#EC4899'
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.2
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 40,
    scale: 0.9
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
export function MarketFocus() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  return <section ref={ref} className="w-full py-32 px-6 bg-navy-900" data-id="element-150">
      <div className="max-w-6xl mx-auto" data-id="element-151">
        <motion.h2 initial={{
        opacity: 0,
        y: 30
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }} className="text-4xl md:text-5xl font-bold mb-12 text-center text-white" data-id="element-152">
          Markets We Explore
        </motion.h2>
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.8,
        delay: 0.2,
        ease: [0.22, 1, 0.36, 1]
      }} className="text-xl text-slate text-center mb-16 max-w-3xl mx-auto leading-relaxed" data-id="element-153">
          We hunt for alpha across traditional and alternative markets,
          combining quantitative rigor with unconventional thinking.
        </motion.p>
        <motion.div variants={containerVariants} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-154">
          {markets.map((market, index) => {
          const Icon = market.icon;
          return <motion.div key={market.name} variants={itemVariants} whileHover={{
            y: -12,
            scale: 1.03,
            transition: {
              duration: 0.3
            }
          }} className="relative group" data-id="element-155">
                <div className="bg-navy-800 border-2 border-navy-700 rounded-lg p-8 h-full hover:border-steel transition-all duration-300 shadow-lg relative overflow-hidden" data-id="element-156">
                  {/* Animated background gradient */}
                  <motion.div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{
                background: `radial-gradient(circle at 50% 50%, ${market.color}10, transparent 70%)`
              }} animate={{
                scale: [1, 1.2, 1],
                opacity: [0, 0.3, 0]
              }} transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'easeInOut'
              }} data-id="element-157" />
                  <motion.div initial={{
                scale: 0,
                rotate: -180
              }} animate={inView ? {
                scale: 1,
                rotate: 0
              } : {}} transition={{
                duration: 0.7,
                delay: 0.4 + index * 0.1
              }} className="mb-6 w-16 h-16 rounded-lg flex items-center justify-center relative z-10" style={{
                backgroundColor: `${market.color}20`
              }} data-id="element-158">
                    <Icon className="w-8 h-8" style={{
                  color: market.color
                }} data-id="element-159" />
                  </motion.div>
                  <h3 className="text-2xl font-bold mb-3 relative z-10" style={{
                color: market.color
              }} data-id="element-160">
                    {market.name}
                  </h3>
                  <p className="text-slate leading-relaxed relative z-10" data-id="element-161">
                    {market.description}
                  </p>
                </div>
                {/* Continuous glow effect */}
                <motion.div className="absolute inset-0 rounded-lg blur-2xl -z-10" style={{
              background: `radial-gradient(circle at center, ${market.color}20, transparent 70%)`
            }} animate={{
              opacity: [0.3, 0.6, 0.3],
              scale: [0.95, 1.05, 0.95]
            }} transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: index * 0.3
            }} data-id="element-162" />
              </motion.div>;
        })}
        </motion.div>
      </div>
    </section>;
}